<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import HrPayrollMark from "@/components/branding/hr-payroll-mark.vue";
import AuthService from "@/services/authService";
import { useAuthStore } from "@/stores/authStore";
import { useAccessStore } from "@/stores/accessStore";
import { useSessionLockStore } from "@/stores/sessionLockStore";
import { useAppLoaderStore } from "@/stores/appLoaderStore";
import { restartIdleLock, getIdleLockTimeoutMs } from "@/services/idleLockService";
import {
  preparePageForSessionLock,
  releasePageFromSessionLock,
} from "@/utils/sessionLockUi";
import { defaultAvatarUrl, resolveAvatarUrl } from "@/utils/imageHelpers";
import { formatLockedDuration } from "@/utils/formatDuration";
import { toast } from "@/utils/toast";

const props = defineProps({
  /** overlay = stay on current page (preserves open modals); page = full route */
  mode: {
    type: String,
    default: "overlay",
    validator: (v) => ["overlay", "page"].includes(v),
  },
});

const authStore = useAuthStore();
const sessionLock = useSessionLockStore();
const route = useRoute();
const router = useRouter();

const showPassword = ref(false);
const password = ref("");
const passwordInput = ref(null);
const loading = ref(false);
const serverMessage = ref("");
const now = ref(Date.now());
let clockTimer = null;

const displayName = computed(() => {
  const user = authStore.user || {};
  const first = user.first_name || "";
  const last = user.last_name || "";
  return [first, last].filter(Boolean).join(" ") || user.email || "User";
});

const avatarSrc = computed(() => {
  const user = authStore.user || {};
  const employeeAvatar = user.employee?.avatar_url;
  return resolveAvatarUrl(employeeAvatar) || defaultAvatarUrl();
});

const redirectTarget = computed(() => {
  const fromQuery = route.query.redirect;
  if (typeof fromQuery === "string" && fromQuery.startsWith("/")) {
    return fromQuery;
  }
  if (sessionLock.returnPath?.startsWith("/")) {
    return sessionLock.returnPath;
  }
  return authStore.isSuperAdmin
    ? "/super-admin/dashboard"
    : "/dashboard/admin-dashboard";
});

const lockedForLabel = computed(() =>
  formatLockedDuration(sessionLock.lockedAt, now.value),
);

const idleTimeoutMinutes = computed(() =>
  Math.round(getIdleLockTimeoutMs() / 60000)
);

async function submitForm() {
  serverMessage.value = "";
  loading.value = true;

  try {
    await AuthService.confirmPassword({ password: password.value });
    sessionLock.unlock();
    password.value = "";
    restartIdleLock();
    useAppLoaderStore().hide();

    try {
      await authStore.fetchCurrentUser?.();
      await useAccessStore().fetchAccess({ force: true });
    } catch (refreshError) {
      console.warn(
        "[session-lock] post-unlock refresh failed:",
        refreshError?.message || refreshError,
      );
    }

    toast("Welcome back");

    if (props.mode === "page") {
      await router.replace(redirectTarget.value);
    }
  } catch (error) {
    const message =
      error?.response?.data?.message || "Password is incorrect.";
    serverMessage.value = message;
    toast(message, "error");
  } finally {
    loading.value = false;
  }
}

function toggleShow() {
  showPassword.value = !showPassword.value;
}

function signInAsDifferentUser() {
  authStore.logout();
  router.push("/login");
}

onMounted(async () => {
  if (props.mode === "overlay") {
    preparePageForSessionLock();
    useAppLoaderStore().hide();
  }

  clockTimer = window.setInterval(() => {
    now.value = Date.now();
  }, 60000);

  await nextTick();
  requestAnimationFrame(() => {
    passwordInput.value?.focus({ preventScroll: true });
  });
});

onUnmounted(() => {
  if (props.mode === "overlay") {
    releasePageFromSessionLock();
  }

  if (clockTimer) {
    window.clearInterval(clockTimer);
  }
});
</script>

<template>
  <Teleport to="body">
    <div
      class="session-lock-overlay"
      :class="{ 'session-lock-overlay--page': mode === 'page' }"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-lock-title"
    >
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-md-5">
            <form @submit.prevent="submitForm">
              <div class="d-flex flex-column justify-content-between vh-100">
                <div class="mx-auto p-4 text-center">
                  <hr-payroll-mark variant="full" />
                </div>
                <div class="card border-primary">
                  <div class="card-body bg-primary bg-opacity-10 rounded p-4">
                    <div class="mb-4 text-center">
                      <div class="mb-2">
                        <span class="badge badge-soft-warning">
                          <i class="ti ti-lock me-1"></i>Session locked
                        </span>
                      </div>
                      <h2 id="session-lock-title" class="mb-2">Welcome back</h2>
                      <p class="text-muted small mb-3">
                        Your session was locked after
                        {{ idleTimeoutMinutes }} minutes of inactivity. Enter your
                        password to continue.
                      </p>
                      <img
                        :src="avatarSrc"
                        alt="Profile"
                        class="img-fluid avatar avatar-xxl rounded-pill my-3"
                      />
                      <h6 class="text-dark mb-1">{{ displayName }}</h6>
                      <p v-if="lockedForLabel" class="text-muted small mb-0">
                        {{ lockedForLabel }}
                      </p>
                    </div>
                    <div v-if="serverMessage" class="alert alert-danger py-2">
                      {{ serverMessage }}
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Password</label>
                      <div class="pass-group">
                        <input
                          ref="passwordInput"
                          :type="showPassword ? 'text' : 'password'"
                          v-model="password"
                          class="pass-input form-control"
                          autocomplete="current-password"
                          required
                        />
                        <span
                          @click="toggleShow"
                          class="ti toggle-password"
                          :class="{
                            'ti-eye': showPassword,
                            'ti-eye-off': !showPassword,
                          }"
                        ></span>
                      </div>
                    </div>
                    <button
                      type="submit"
                      class="btn btn-primary w-100 mb-2"
                      :disabled="loading"
                    >
                      {{ loading ? "Unlocking..." : "Unlock session" }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary w-100 btn-sm"
                      :disabled="loading"
                      @click="signInAsDifferentUser"
                    >
                      Sign in as a different user
                    </button>
                  </div>
                </div>
                <div class="p-4 text-center">
                  <p class="mb-0 text-gray-9">
                    2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR
                    &amp; Payroll
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.session-lock-overlay {
  position: fixed;
  inset: 0;
  z-index: 99990;
  overflow-y: auto;
  pointer-events: auto;
  isolation: isolate;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.session-lock-overlay--page {
  position: relative;
  inset: auto;
  z-index: auto;
  min-height: 100vh;
}
</style>
